import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Chip, Avatar, Checkbox, alpha,
} from '@mui/material';
import {
  Edit          as EditIcon,
  Archive       as ArchiveIcon,
  Link          as LinkIcon,
  Description   as DescriptionIcon,
  Business      as BusinessIcon,
  Flag          as FlagIcon,
  Group         as GroupIcon,
  TaskAlt       as TaskSidebarIcon,
  OpenInNew     as OpenInNewIcon,
  CheckCircle   as CheckCircleIcon,
  TrendingUp    as ProgressIcon,
  Notes         as NotesIcon,
} from '@mui/icons-material';
import api                          from '../../../services/api';
import { useAuth }                  from '../../../contexts/AuthContext';
import { canArchiveTask, canFullEditTask, canEditTaskMilestones } from '../../../utils/taskPermissions';
import { resolveUserId, userDetailsPath } from '../../../utils/userDetailsLink';
import Header                       from '../../common/Header';
import Loading                      from '../../common/Loading';
import FeedbackMessage              from '../../common/FeedbackMessage';
import ConfirmDeleteDialog          from '../../common/ConfirmDeleteDialog';
import { injectDetailsPageStyles }  from '../../common/DetailsPagePrimitives';
import SystemInformationSection     from '../../common/SystemInformationSection';
import { resolveTaskStatus, getTaskStatusMeta } from '../../../utils/taskStatus';
import { formatSystemDateTime } from '../../../utils/systemDateFormat';

// ─── Design tokens ────────────────────────────────────────────────────────────
const NEON        = '#38BDF8';
const NEON2       = '#818CF8';
const NEON3       = '#34D399';
const SURFACE_MID = '#0a1628';
const SURFACE_UP  = '#0f1f38';
const TEXT_BODY   = '#E2E8F0';
const TEXT_MUTED  = '#64748B';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getProgressColor = (p) => {
  if (p >= 80) return NEON3;
  if (p >= 40) return '#FCD34D';
  return '#F87171';
};

const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '-';

const normalizeTask = (taskData) => ({
  ...taskData,
  users:       Array.isArray(taskData?.users)       ? taskData.users       : [],
  departments: Array.isArray(taskData?.departments) ? taskData.departments : [],
  milestones:  Array.isArray(taskData?.milestones)  ? taskData.milestones  : [],
  progress:    Number.isFinite(Number(taskData?.progress)) ? Number(taskData.progress) : 0,
  links:       Array.isArray(taskData?.links)       ? taskData.links       : [],
});

// ─── MetricCard — identical to ParcelDetails ─────────────────────────────────
const MetricCard = ({ label, value, icon: Icon, color = NEON, sub }) => (
  <Box sx={{
    position: 'relative',
    borderRadius: '14px', px: 2.5, py: 2,
    background: `linear-gradient(145deg, ${alpha(color, 0.09)} 0%, ${alpha(SURFACE_UP, 0.6)} 100%)`,
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 0 24px ${alpha(color, 0.07)}, inset 0 1px 0 ${alpha(color, 0.1)}`,
    overflow: 'hidden',
  }}>
    <Box sx={{ position: 'absolute', top: -16, right: -16, width: 70, height: 70, borderRadius: '50%', background: alpha(color, 0.08), filter: 'blur(18px)', pointerEvents: 'none' }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.8 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: alpha(color, 0.75) }} />}
      <Typography sx={{ fontSize: '0.67rem', fontFamily: "'DM Sans', sans-serif", color: TEXT_MUTED, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color, letterSpacing: 0.3, lineHeight: 1.15 }}>
      {value || '—'}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.7rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", mt: 0.4 }}>{sub}</Typography>}
  </Box>
);

// ─── Section — identical to ParcelDetails ────────────────────────────────────
const Section = ({ title, icon: Icon, accent = NEON, delay = 0, children }) => (
  <Box sx={{
    borderRadius: '16px',
    border: `1px solid ${alpha(accent, 0.14)}`,
    background: `linear-gradient(160deg, ${alpha(SURFACE_UP, 0.75)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
    overflow: 'hidden',
    animation: `fadeUp 0.4s ease ${delay}s both`,
    '@keyframes fadeUp': {
      from: { opacity: 0, transform: 'translateY(10px)' },
      to:   { opacity: 1, transform: 'translateY(0)' },
    },
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2,
      px: 2.5, py: 1.5,
      borderBottom: `1px solid ${alpha(accent, 0.1)}`,
      background: alpha(accent, 0.04),
    }}>
      <Box sx={{
        width: 26, height: 26, borderRadius: '7px',
        background: alpha(accent, 0.12),
        border: `1px solid ${alpha(accent, 0.22)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 13, color: accent }} />
      </Box>
      <Typography sx={{
        fontFamily: "'DM Sans', sans-serif", fontWeight: 700,
        fontSize: '0.78rem', color: alpha(TEXT_BODY, 0.85),
        letterSpacing: '0.07em', textTransform: 'uppercase',
      }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ p: 2.5 }}>{children}</Box>
  </Box>
);

// ─── InfoRow — identical to ParcelDetails ────────────────────────────────────
const InfoRow = ({ icon: Icon, label, value, color = NEON, emptyLabel = 'Not specified', fullWidth = false, onClick }) => {
  const display = value || emptyLabel;
  const isEmpty = !value;
  const isInteractive = Boolean(onClick && !isEmpty);
  return (
    <Box
      role={isInteractive ? 'link' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={isInteractive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(e); } } : undefined}
      sx={{
        gridColumn: fullWidth ? '1 / -1' : undefined,
        display: 'flex', alignItems: 'center', gap: 1.5,
        py: 0.9, px: 1.2, borderRadius: '9px',
        cursor: isInteractive ? 'pointer' : 'default',
        transition: 'background 0.15s',
        '&:hover': isInteractive ? { background: alpha(color, 0.06) } : {},
      }}
    >
      <Box sx={{
        width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
        background: alpha(color, isEmpty ? 0.05 : 0.1),
        border: `1px solid ${alpha(color, isEmpty ? 0.1 : 0.18)}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon sx={{ fontSize: 14, color: isEmpty ? TEXT_MUTED : alpha(color, 0.85) }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.65rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography sx={{
          fontSize: '0.88rem',
          color: isEmpty ? TEXT_MUTED : (isInteractive ? color : TEXT_BODY),
          fontStyle: isEmpty ? 'italic' : 'normal',
          fontFamily: "'DM Sans', sans-serif", fontWeight: isEmpty ? 400 : 500,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {display}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Progress bar ─────────────────────────────────────────────────────────────
const ProgressBar = ({ progress }) => {
  const color = getProgressColor(progress);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ flex: 1, height: 4, borderRadius: 2, background: alpha('#fff', 0.07), overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${Math.min(progress, 100)}%`, borderRadius: 2, background: color, transition: 'width 0.6s ease' }} />
      </Box>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color, minWidth: 30, textAlign: 'right' }}>
        {progress}%
      </Typography>
    </Box>
  );
};

// ─── Milestone list ───────────────────────────────────────────────────────────
const MilestoneList = ({ milestones, canToggle, onToggle, updatingIndex }) => {
  if (!milestones?.length) return (
    <Typography sx={{ fontSize: '0.82rem', color: alpha(TEXT_MUTED, 0.45), fontFamily: "'DM Sans', sans-serif", fontStyle: 'italic' }}>
      No milestones defined.
    </Typography>
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {milestones.map((m, i) => {
        const milestone = typeof m === 'string' ? { text: m, done: false } : m;
        return (
          <Box key={i} sx={{
            display: 'flex', gap: 1.25, alignItems: 'flex-start',
            py: 1, borderBottom: `1px solid ${alpha('#fff', 0.04)}`,
            '&:last-child': { borderBottom: 'none' },
          }}>
            <Box sx={{
              width: 20, height: 20, borderRadius: '50%', flexShrink: 0, mt: '2px',
              background: milestone.done ? alpha(NEON3, 0.15) : alpha('#fff', 0.05),
              border: `1px solid ${alpha(milestone.done ? NEON3 : TEXT_MUTED, milestone.done ? 0.4 : 0.18)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: milestone.done ? NEON3 : TEXT_MUTED, fontFamily: "'Rajdhani', sans-serif" }}>
                {i + 1}
              </Typography>
            </Box>
            <Typography sx={{
              flex: 1, fontSize: '0.84rem', lineHeight: 1.55, pt: '1px',
              fontFamily: "'DM Sans', sans-serif",
              color: milestone.done ? alpha(NEON3, 0.75) : alpha(TEXT_BODY, 0.82),
              textDecoration: milestone.done ? 'line-through' : 'none',
              textDecorationColor: alpha(NEON3, 0.4),
            }}>
              {milestone.text}
            </Typography>
            <Checkbox
              checked={Boolean(milestone.done)}
              disabled={!canToggle || updatingIndex === i}
              onChange={(e) => onToggle(i, e.target.checked)}
              icon={<CheckCircleIcon sx={{ color: alpha(TEXT_MUTED, 0.3), fontSize: 18 }} />}
              checkedIcon={<CheckCircleIcon sx={{ color: NEON3, fontSize: 18 }} />}
              sx={{ p: 0.25, flexShrink: 0, mt: '-1px' }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

// ─── TaskDetails ──────────────────────────────────────────────────────────────
const TaskDetails = () => {
  const [task,                   setTask]                   = useState(null);
  const [loading,                setLoading]                = useState(true);
  const [error,                  setError]                  = useState(null);
  const [successMessage,         setSuccessMessage]         = useState(null);
  const [archiveDialogOpen,      setArchiveDialogOpen]      = useState(false);
  const [updatingMilestoneIndex, setUpdatingMilestoneIndex] = useState(null);

  const navigate              = useNavigate();
  const { id }                = useParams();
  const { user: currentUser } = useAuth();

  useEffect(() => { injectDetailsPageStyles(); }, []);
  useEffect(() => { fetchTask(); }, [id]);

  const fetchTask = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/tasks/${id}`);
      setTask(normalizeTask(res.data));
    } catch (err) {
      setError(
        err.response?.status === 403
          ? 'You do not have permission to view this task.'
          : 'Error fetching task details. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveTask = async () => {
    try {
      const taskTitle = task?.title || 'Task';
      await api.post(`/tasks/${id}/archive`);
      setArchiveDialogOpen(false);
      setSuccessMessage(`Task "${taskTitle}" has been archived successfully.`);
      setTimeout(() => navigate('/tasks', { state: { refreshList: Date.now() } }), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error archiving task.');
      setArchiveDialogOpen(false);
    }
  };

  const handleToggleMilestone = async (index, done) => {
    try {
      setUpdatingMilestoneIndex(index);
      const res = await api.patch(`/tasks/${id}/milestones`, { index, done });
      setTask(normalizeTask(res.data));
      setSuccessMessage('Milestone updated.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating milestone.');
    } finally {
      setUpdatingMilestoneIndex(null);
    }
  };

  if (loading || !task) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message="Loading task details" size={56} variant="primary" />
    </Box>
  );

  const boardStatus    = getTaskStatusMeta(resolveTaskStatus(task));
  const progressColor  = getProgressColor(task.progress);
  const milestoneDone  = task.milestones.filter((m) => typeof m !== 'string' && Boolean(m?.done)).length;
  const milestoneTotal = task.milestones.length;
  const hasLinks       = task.links?.length > 0 || task.documentation_link || task.project_link;

  const createdByName = task.createdBy?.name || task.creator?.name || task.created_by_name || (typeof task.created_by === 'object' ? task.created_by?.name : null) || null;
  const updatedByName = task.updatedBy?.name || task.updater?.name || task.updated_by_name || (typeof task.updated_by === 'object' ? task.updated_by?.name : null) || null;

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      <Header
        title="Task Details"
        subtitle="View task information"
        icon={TaskSidebarIcon}
        showBackButton
        onBackClick={() => navigate('/tasks')}
        actionButton={
          (canFullEditTask(currentUser, task) || canArchiveTask(currentUser, task)) && (
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {canFullEditTask(currentUser, task) && (
                <Button startIcon={<EditIcon sx={{ fontSize: 16 }} />} onClick={() => navigate(`/tasks/edit/${id}`)}
                  sx={{ borderRadius: '10px', px: 2.5, py: 0.7, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem', background: 'transparent', border: `1.5px solid ${alpha(NEON, 0.6)}`, color: NEON, transition: 'all 0.2s ease', '&:hover': { background: alpha(NEON, 0.08), borderColor: NEON, boxShadow: `0 0 12px ${alpha(NEON, 0.2)}` } }}>
                  Edit
                </Button>
              )}
              {canArchiveTask(currentUser, task) && (
                <Button startIcon={<ArchiveIcon sx={{ fontSize: 16 }} />} onClick={() => setArchiveDialogOpen(true)}
                  sx={{ borderRadius: '10px', px: 2.5, py: 0.7, fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem', background: 'transparent', border: `1.5px solid ${alpha('#F87171', 0.6)}`, color: '#F87171', transition: 'all 0.2s ease', '&:hover': { background: alpha('#F87171', 0.08), borderColor: '#F87171', boxShadow: `0 0 12px ${alpha('#F87171', 0.2)}` } }}>
                  Archive
                </Button>
              )}
            </Box>
          )
        }
      />

      <FeedbackMessage message={error} severity="error" onClose={() => setError(null)}
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', boxShadow: '0 4px 20px rgba(239,68,68,0.12)', color: '#fca5a5', mb: 3 }} />
      <FeedbackMessage message={successMessage} severity="success" onClose={() => setSuccessMessage(null)}
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(NEON3, 0.1), border: `1px solid ${alpha(NEON3, 0.3)}`, color: alpha(NEON3, 0.95), mb: 2 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <Box sx={{
          borderRadius: '16px',
          border: `1px solid ${alpha(NEON, 0.15)}`,
          background: `linear-gradient(135deg, ${alpha(SURFACE_UP, 0.9)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
          p: { xs: 2, md: 2.5 },
          animation: 'fadeUp 0.35s ease both',
          '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}>
          {/* Title + status badge */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '13px', flexShrink: 0,
                background: `linear-gradient(135deg, ${alpha(NEON2, 0.25)}, ${alpha(NEON, 0.15)})`,
                border: `2px solid ${alpha(NEON2, 0.3)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <TaskSidebarIcon sx={{ fontSize: 22, color: NEON2 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", color: TEXT_BODY, letterSpacing: 0.3, lineHeight: 1.15 }}>
                  {task.title || 'Untitled Task'}
                </Typography>
                {task.departments?.length > 0 && (
                  <Typography sx={{ fontSize: '0.78rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif" }}>
                    {task.departments.map((d) => d.name).join(', ')}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.7,
              px: 1.4, py: 0.5, borderRadius: '20px',
              background: alpha(boardStatus.color, 0.1),
              border: `1px solid ${alpha(boardStatus.color, 0.3)}`,
              alignSelf: 'flex-start',
            }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: boardStatus.color }} />
              <Typography sx={{ fontSize: '0.75rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: boardStatus.color, letterSpacing: '0.05em' }}>
                {boardStatus.label}
              </Typography>
            </Box>
          </Box>

          {/* Metric cards — 4 columns */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
            <MetricCard label="Status"     value={boardStatus.label}    icon={TaskSidebarIcon} color={boardStatus.color} />
            <MetricCard label="Progress"   value={`${task.progress}%`}  icon={ProgressIcon}    color={progressColor} />
            <MetricCard
              label="Milestones"
              value={milestoneTotal > 0 ? `${milestoneDone} / ${milestoneTotal}` : null}
              icon={FlagIcon}
              color={NEON3}
              sub={milestoneTotal > 0 ? 'completed' : undefined}
            />
            <MetricCard
              label="Assigned"
              value={task.users.length > 0 ? `${task.users.length} ${task.users.length === 1 ? 'member' : 'members'}` : null}
              icon={GroupIcon}
              color={NEON2}
            />
          </Box>
        </Box>

        {/* ── Description + Details side-by-side ───────────────────────────── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>

          {/* Description */}
          <Section title="Description" icon={NotesIcon} accent={NEON} delay={0.06}>
            <Typography sx={{
              fontSize: '0.88rem',
              color: task.description ? alpha(TEXT_BODY, 0.78) : TEXT_MUTED,
              fontFamily: "'DM Sans', sans-serif",
              lineHeight: 1.8,
              fontStyle: task.description ? 'normal' : 'italic',
            }}>
              {task.description || 'No description provided.'}
            </Typography>

            {/* Links inside description panel */}
            {hasLinks && (
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(NEON, 0.08)}` }}>
                <Typography sx={{ fontSize: '0.65rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", textTransform: 'uppercase', letterSpacing: '0.09em', fontWeight: 600, mb: 1 }}>
                  Links
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {task.documentation_link && (
                    <Box onClick={() => window.open(task.documentation_link, '_blank')}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, px: 1, borderRadius: '8px', cursor: 'pointer', transition: 'background 0.15s', '&:hover': { background: alpha(NEON, 0.06) } }}>
                      <DescriptionIcon sx={{ fontSize: 14, color: alpha(NEON, 0.55), flexShrink: 0 }} />
                      <Typography sx={{ flex: 1, fontSize: '0.8rem', color: alpha(NEON, 0.8), fontFamily: "'DM Sans', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Documentation</Typography>
                      <OpenInNewIcon sx={{ fontSize: 11, color: alpha(NEON, 0.35), flexShrink: 0 }} />
                    </Box>
                  )}
                  {task.project_link && (
                    <Box onClick={() => window.open(task.project_link, '_blank')}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, px: 1, borderRadius: '8px', cursor: 'pointer', transition: 'background 0.15s', '&:hover': { background: alpha(NEON, 0.06) } }}>
                      <LinkIcon sx={{ fontSize: 14, color: alpha(NEON, 0.55), flexShrink: 0 }} />
                      <Typography sx={{ flex: 1, fontSize: '0.8rem', color: alpha(NEON, 0.8), fontFamily: "'DM Sans', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Project</Typography>
                      <OpenInNewIcon sx={{ fontSize: 11, color: alpha(NEON, 0.35), flexShrink: 0 }} />
                    </Box>
                  )}
                  {task.links.map((link, i) => (
                    <Box key={i} onClick={() => window.open(link.url, '_blank')}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.6, px: 1, borderRadius: '8px', cursor: 'pointer', transition: 'background 0.15s', '&:hover': { background: alpha(NEON, 0.06) } }}>
                      <LinkIcon sx={{ fontSize: 14, color: alpha(NEON, 0.55), flexShrink: 0 }} />
                      <Typography sx={{ flex: 1, fontSize: '0.8rem', color: alpha(NEON, 0.8), fontFamily: "'DM Sans', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link.title || link.url}</Typography>
                      <OpenInNewIcon sx={{ fontSize: 11, color: alpha(NEON, 0.35), flexShrink: 0 }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Section>

          {/* Team */}
          <Section title="Assigned Team" icon={GroupIcon} accent={NEON2} delay={0.12}>
            {task.users.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {task.users.map((u) => {
                  const userId = resolveUserId(u);
                  const initials = u?.name ? u.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() : '?';
                  const canNav = Boolean(userId);
                  return (
                    <Box
                      key={u.id}
                      role={canNav ? 'link' : undefined}
                      tabIndex={canNav ? 0 : undefined}
                      onClick={canNav ? () => navigate(userDetailsPath(userId)) : undefined}
                      onKeyDown={canNav ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(userDetailsPath(userId)); } } : undefined}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        py: 0.9, px: 1.2, borderRadius: '9px',
                        cursor: canNav ? 'pointer' : 'default',
                        transition: 'background 0.15s',
                        '&:hover': canNav ? { background: alpha(NEON2, 0.06) } : {},
                      }}
                    >
                      <Avatar sx={{
                        width: 30, height: 30, fontSize: '0.7rem', fontWeight: 700,
                        background: alpha(NEON2, 0.15), border: `1px solid ${alpha(NEON2, 0.25)}`,
                        color: NEON2, fontFamily: "'DM Sans', sans-serif", flexShrink: 0,
                      }}>
                        {initials}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.88rem', color: canNav ? NEON2 : alpha(TEXT_BODY, 0.85), fontFamily: "'DM Sans', sans-serif", fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {u.name}
                        </Typography>
                        {u.email && (
                          <Typography sx={{ fontSize: '0.72rem', color: TEXT_MUTED, fontFamily: "'DM Sans', sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {u.email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <InfoRow icon={GroupIcon} label="Team" value={null} color={NEON2} emptyLabel="No users assigned" />
            )}
          </Section>
        </Box>

        {/* ── Milestones (full width) ───────────────────────────────────────── */}
        <Section title="Milestones" icon={FlagIcon} accent={NEON3} delay={0.18}>
          <MilestoneList
            milestones={task.milestones}
            canToggle={canEditTaskMilestones(currentUser, task)}
            onToggle={handleToggleMilestone}
            updatingIndex={updatingMilestoneIndex}
          />
        </Section>

        {/* ── System information ────────────────────────────────────────────── */}
        <SystemInformationSection
          createdBy={createdByName || 'Not available'}
          createdAt={formatSystemDateTime(task.created_at)}
          updatedBy={updatedByName || 'Not available'}
          updatedAt={formatSystemDateTime(task.updated_at)}
          delay={0.22}
          accentColor={NEON}
          surfaceUp={SURFACE_UP}
          surfaceMid={SURFACE_MID}
          textBody={TEXT_BODY}
          textMuted={TEXT_MUTED}
        />

      </Box>

      <ConfirmDeleteDialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        onConfirm={handleArchiveTask}
        itemType="task"
        itemName={task?.title}
        title="Confirm Archive"
        description="The task will be archived (soft-deleted). It can be restored from the database if needed."
        confirmLabel="Archive task"
      />
    </Container>
  );
};

export default TaskDetails;
